import { SessionStatus } from '@/types';

import type { CalendarAppointmentProjection } from './calendarAppointmentProjection';
import type {
  BrowserAppointmentView,
  BrowserExpectedIdentity,
  DecodedBrowserAppointment,
  ParticipantAppointment,
  ParticipantAppointmentDetail,
} from './types';
import type { Session } from '@/types';

// Mirrors AppointmentCard.isHttpsAppointmentUrl without importing that file
// (this module is a plain adapter and should stay React-free).
const isHttpsJoinUrl = (value: string | null): boolean => {
  if (!value) return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

// A native Appointment should surface its join link only when Backend says
// join is currently allowed, the URL is a real HTTPS URL, the appointment
// is not cancelled or in a cancellation_pending lifecycle, and its
// scheduled end has not passed. This mirrors what MemberAppointmentRow
// does directly; because the adapter is what SessionCard consumes on
// /sessions/upcoming, gating here prevents SessionCard's legacy "Join
// session" CTA (which only checks HTTPS location plus isToday) from
// firing on a native row where join is not renderable.
//
// Exported so /sessions/upcoming's reconciliation path can scrub a legacy
// Session projection's stale `location` when the corresponding native says
// join is no longer valid. The optional `now` argument keeps the endAt
// check testable and lets the caller thread a ticking clock through the
// predicate without re-reading `Date.now()` on every call.
export const canRenderNativeJoin = (
  appointment: CalendarAppointmentProjection | NativeTimelineAppointment,
  now: number = Date.now(),
): boolean => {
  if (!appointment.actionCapabilities.join.allowed) return false;
  if (!isHttpsJoinUrl(appointment.meeting.joinUrl)) return false;
  if (
    appointment.lifecycleStatus === 'cancelled' ||
    appointment.lifecycleStatus === 'cancellation_pending'
  )
    return false;
  if (Date.parse(appointment.currentSchedule.endAt) < now) return false;
  return true;
};

export type NativeTimelineAppointment = Pick<
  BrowserAppointmentView,
  | 'actionCapabilities'
  | 'currentSchedule'
  | 'lifecycleStatus'
  | 'meeting'
  | 'seriesId'
  | 'version'
> & {
  seriesMutationVersion: number;
};

export const appointmentSessionKind = (
  appointment: Pick<BrowserAppointmentView, 'kind' | 'seriesId'>,
): Session['kind'] => {
  if (appointment.kind === 'chemistry') return 'chemistry';
  return appointment.seriesId === null ? 'single' : 'biweekly';
};

// Adapts a native Moments Appointment into the `Session` shape the existing
// MentoCal cancel/reschedule dialogs require. Extracted from
// `src/components/Sessions/Member/SessionTimeline/index.tsx` so the new
// `/m/appointments` page can reuse it without pulling in the timeline.
//
// Person ids from `expectedIdentities` become `coach.id` and `user.id` /
// `coachId` / `userId`, matching the person-id convention Moments uses for
// scheduling-context and lifecycle commands. Rails coach `User.id` is not used
// here.
export const actionUser = (
  identity: CalendarAppointmentProjection['coach'] | BrowserExpectedIdentity,
): Session['coach'] => {
  const [firstName = identity.displayName, ...lastNameParts] =
    identity.displayName.split(' ');
  return {
    avatarUrl:
      'avatarUrl' in identity ? (identity.avatarUrl ?? undefined) : undefined,
    cometChatId: '',
    firstName,
    fullName: identity.displayName,
    id: 'personId' in identity ? identity.personId : identity.id,
    lastName: lastNameParts.join(' '),
  } as Session['coach'];
};

export const appointmentActionSession = (
  appointment:
    | CalendarAppointmentProjection
    | DecodedBrowserAppointment
    | ParticipantAppointment
    | ParticipantAppointmentDetail,
): Session => {
  const coach =
    'expectedIdentities' in appointment
      ? appointment.expectedIdentities.coach
      : appointment.coach;
  const member =
    'expectedIdentities' in appointment
      ? appointment.expectedIdentities.member
      : appointment.member;
  const cancelCapabilities = appointment.actionCapabilities.cancel;
  return {
    bookId: `moments:${appointment.id}`,
    bookingSource: 'moments',
    coach: actionUser(coach),
    coachId: 'personId' in coach ? coach.personId : coach.id,
    createdAt: appointment.baselineSchedule.startAt,
    endedAt: appointment.currentSchedule.endAt,
    id: appointment.id,
    isChemistryCall: appointment.kind === 'chemistry',
    kind: appointmentSessionKind(appointment),
    location: canRenderNativeJoin(appointment)
      ? (appointment.meeting.joinUrl ?? undefined)
      : undefined,
    recurringId: appointment.seriesId ?? '',
    slug: appointment.kind,
    sourceAppointmentCancelAllowed:
      appointment.actionCapabilities.cancel.allowed &&
      appointment.actionCapabilities.cancel.scopes.includes('appointment'),
    sourceAppointmentCancelInitiatorRequired:
      'initiatorRequired' in cancelCapabilities
        ? cancelCapabilities.initiatorRequired
        : false,
    sourceAppointmentCancelNearTermConfirmationRequired:
      'nearTermConfirmationRequired' in cancelCapabilities
        ? cancelCapabilities.nearTermConfirmationRequired
        : false,
    sourceAppointmentCancelOpsNoteRequired:
      'opsNoteRequired' in cancelCapabilities
        ? cancelCapabilities.opsNoteRequired
        : false,
    sourceAppointmentCancelReasonRequired:
      appointment.actionCapabilities.cancel.reasonRequired,
    sourceAppointmentCancelScopes: appointment.actionCapabilities.cancel.scopes,
    sourceAppointmentId: appointment.id,
    sourceAppointmentIdentityScheme: 'moments-person',
    sourceAppointmentRescheduleAllowed:
      appointment.actionCapabilities.reschedule.allowed &&
      appointment.actionCapabilities.reschedule.scopes.includes('appointment'),
    sourceAppointmentRescheduleScopes:
      appointment.actionCapabilities.reschedule.scopes,
    sourceAppointmentSeriesId: appointment.seriesId,
    sourceAppointmentSeriesMutationVersion: appointment.seriesMutationVersion,
    sourceAppointmentVersion: appointment.version,
    startedAt: appointment.currentSchedule.startAt,
    status:
      appointment.lifecycleStatus === 'cancelled'
        ? SessionStatus.CANCELLED
        : SessionStatus.ACTIVE,
    title:
      appointment.kind === 'chemistry' ? 'Chemistry Call' : 'Coaching Session',
    user: actionUser(member),
    userId: 'personId' in member ? member.personId : member.id,
  } as Session;
};
